import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useUpsells } from "@/hooks/use-upsells";
import { useHighlight } from "@/hooks/use-highlight";
import { useFreelancerProjectsEnabled } from "@/hooks/use-freelancer-projects";
import { useCanLinkFreelanceSuppliers, useFreelancerSupplierLinks } from "@/hooks/use-freelancer-suppliers";
import { formatNOK, formatDate } from "@/lib/format";
import type { DBFreelancerSupplier, DBVoucher } from "@/lib/db-types";
import type { BudgetLineRow, VoucherAllocationRow } from "@/hooks/use-project-detail";
import { deriveLockedVoucherIds } from "@/lib/voucher-locks";
import { SrIcon } from "@/components/ui/SrIcon";
import VoucherToUpsellDialog from "./VoucherToUpsellDialog";
import { VoucherAllocationEditor } from "./VoucherAllocationEditor";
import { SupplierFreelancerLink } from "@/components/freelancers/SupplierFreelancerLink";

interface VoucherTableProps {
  vouchers: DBVoucher[];
  budgetLines: BudgetLineRow[];
  voucherAllocations?: VoucherAllocationRow[];
  projectId: string;
  onOpenDocument: (v: DBVoucher) => void;
  /** VIB-2930: samme gate som godkjenning (approve_vouchers). Uten den vises fordelingen bare som tekst. */
  canAllocate: boolean;
}

interface VoucherRowProps {
  voucher: DBVoucher;
  budgetLines: BudgetLineRow[];
  allocations: VoucherAllocationRow[];
  isUpsold: boolean;
  /** VIB-1197: reversering/original er låst — kan ikke fordeles. */
  lockedForAllocation: boolean;
  canAllocate: boolean;
  /** VIB-2927: null = flagget er av. */
  supplierLinks: { links: DBFreelancerSupplier[]; canLink: boolean } | null;
  onUpsell: () => void;
  onOpenDocument: () => void;
  onAllocate: () => void;
}

/**
 * VIB-997: bilag fordeles via `voucher_allocations` (flere linjer per bilag),
 * ikke lenger via den gamle én-FK `vouchers.budget_line_id`. Vis fordelingen
 * basert på allokeringene som har en budsjettlinje satt.
 */
function buildAllocationLabel(
  allocations: VoucherAllocationRow[],
  budgetLines: BudgetLineRow[],
): string {
  const assigned = allocations.filter((a) => a.budget_line_id);
  if (assigned.length === 0) return "Fordel";

  const unassignedCount = allocations.length - assigned.length;
  const partial = unassignedCount > 0 ? " (delvis)" : "";

  if (assigned.length === 1) {
    const bl = budgetLines.find((b) => b.id === assigned[0].budget_line_id);
    if (bl) return `${bl.phase} – ${bl.description}${partial}`;
    return `1 linje${partial}`;
  }
  return `${assigned.length} linjer${partial}`;
}

/** Fullt fordelt = minst én allokering, og alle har en budsjettlinje. */
function isFullyAllocated(allocations: VoucherAllocationRow[]): boolean {
  return allocations.length > 0 && allocations.every((a) => a.budget_line_id);
}

/**
 * VIB-1236: lønnsjournaler er interne posteringer uten beskrivelse eller
 * dokument fra PO. Vis en meningsfull etikett i stedet for tomt.
 */
function voucherDescription(v: DBVoucher): string {
  if (v.description) return v.description;
  if (v.voucher_type === "PayrollJournal") return "Lønnskjøring (innleid arbeidskraft)";
  return "—";
}

function VoucherRow({
  voucher: v,
  budgetLines,
  allocations,
  isUpsold,
  lockedForAllocation,
  canAllocate,
  supplierLinks,
  onUpsell,
  onOpenDocument,
  onAllocate,
}: VoucherRowProps) {
  const highlightRef = useHighlight<HTMLTableRowElement>(v.id, "voucherId");
  const allocationLabel = buildAllocationLabel(allocations, budgetLines);
  const fullyAllocated = isFullyAllocated(allocations);

  return (
    <tr ref={highlightRef} className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors group">
      <td className="py-2.5 px-4 tabular-nums">{v.voucher_no ?? "—"}</td>
      <td className="py-2.5 px-4">
        <div>{v.supplier_name ?? "—"}</div>
        {supplierLinks && <SupplierFreelancerLink voucher={v} links={supplierLinks.links} canLink={supplierLinks.canLink} />}
      </td>
      <td className="py-2.5 px-4 max-w-[200px] truncate">{voucherDescription(v)}</td>
      <td className="py-2.5 px-4 text-xs text-muted-foreground max-w-[180px]">
        {lockedForAllocation ? (
          // VIB-1197: reversering/original er låst — kan ikke fordeles, men vises
          // fortsatt i lista for sporbarhet.
          <span
            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
            title="Dette bilaget er en reversering eller en original som er reversert i PowerOffice. Det inngår ikke i kostnadsgrunnlaget og kan ikke fordeles."
          >
            <SrIcon name="lock" className="w-3 h-3 shrink-0" />
            Reversert/korrigert — låst
          </span>
        ) : !canAllocate ? (
          <span className="flex items-center gap-1">
            {fullyAllocated && <SrIcon name="check" className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
            {allocationLabel === "Fordel" ? "Ikke fordelt" : allocationLabel}
          </span>
        ) : (
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground text-left"
            onClick={onAllocate}
            title="Klikk for å fordele bilaget på budsjettlinjer"
          >
            {fullyAllocated && (
              <SrIcon name="check" className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            )}
            {allocationLabel}
            <SrIcon name="pencil" className="w-3 h-3 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity shrink-0" />
          </button>
        )}
      </td>
      <td className="py-2.5 px-4 text-right tabular-nums font-medium">{formatNOK(v.amount)}</td>
      <td className="py-2.5 px-4 text-muted-foreground">{formatDate(v.voucher_date)}</td>
      <td className="py-2.5 px-4">
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-primary gap-1"
            title="Se bilag"
            onClick={onOpenDocument}
          >
            <SrIcon name="file-text" className="w-3.5 h-3.5"  />
            PDF
          </Button>
          {isUpsold ? (
            <span
              className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-primary/10 text-primary"
              title="Mersalg er allerede opprettet fra dette bilaget"
            >
              <SrIcon name="check" className="w-3 h-3" />
              Viderefakturert
            </span>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-primary gap-1"
              title="Viderefakturer som mersalg"
              onClick={onUpsell}
            >
              <SrIcon name="plus" className="w-3.5 h-3.5" />
              Som mersalg
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function VoucherTable({ vouchers, budgetLines, voucherAllocations = [], projectId, onOpenDocument, canAllocate }: VoucherTableProps) {
  const [allocatingVoucher, setAllocatingVoucher] = useState<DBVoucher | null>(null);
  const [upsellTarget, setUpsellTarget] = useState<DBVoucher | null>(null);
  const { data: upsells } = useUpsells(projectId);
  const freelancersOn = useFreelancerProjectsEnabled();
  const { data: supplierLinkRows = [] } = useFreelancerSupplierLinks(freelancersOn);
  const canLinkSuppliers = useCanLinkFreelanceSuppliers(freelancersOn);
  const supplierLinks = freelancersOn ? { links: supplierLinkRows, canLink: canLinkSuppliers } : null;
  const upsoldVoucherIds = new Set(
    (upsells ?? []).map((u) => u.source_voucher_id).filter((id): id is string => !!id),
  );

  // VIB-997: grupper allokeringene per bilag for å vise fordelingsstatus i lista.
  const allocationsByVoucher = new Map<string, VoucherAllocationRow[]>();
  for (const a of voucherAllocations) {
    const list = allocationsByVoucher.get(a.voucher_id);
    if (list) list.push(a);
    else allocationsByVoucher.set(a.voucher_id, [a]);
  }

  // VIB-1197: avled hvilke bilag som er reverseringer eller reverserte
  // originaler. De vises fortsatt, men kan ikke fordeles.
  const lockedVoucherIds = deriveLockedVoucherIds(vouchers);

  return (
    <div className="border-t">
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                Nr
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                Leverandør
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                Beskrivelse
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                Budsjettlinje
              </th>
              <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                Beløp
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                Dato
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {vouchers.map((v) => (
              <VoucherRow
                key={v.id}
                voucher={v}
                budgetLines={budgetLines}
                allocations={allocationsByVoucher.get(v.id) ?? []}
                isUpsold={upsoldVoucherIds.has(v.id)}
                lockedForAllocation={lockedVoucherIds.has(v.id)}
                canAllocate={canAllocate}
                supplierLinks={supplierLinks}
                onUpsell={() => setUpsellTarget(v)}
                onOpenDocument={() => onOpenDocument(v)}
                onAllocate={() => setAllocatingVoucher(v)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <VoucherToUpsellDialog
        voucher={upsellTarget}
        projectId={projectId}
        open={!!upsellTarget}
        onOpenChange={(open) => !open && setUpsellTarget(null)}
      />
      <Dialog
        open={!!allocatingVoucher}
        onOpenChange={(open) => !open && setAllocatingVoucher(null)}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fordel bilag</DialogTitle>
            <DialogDescription>
              {allocatingVoucher
                ? `${allocatingVoucher.supplier_name ?? "Bilag"} – ${formatNOK(allocatingVoucher.amount)}`
                : null}
            </DialogDescription>
          </DialogHeader>
          {allocatingVoucher && (
            <VoucherAllocationEditor
              voucherId={allocatingVoucher.id}
              voucherTotal={allocatingVoucher.amount ?? 0}
              projectId={projectId}
              budgetLines={budgetLines}
              documentTitle={
                allocatingVoucher.supplier_name ??
                `Bilag #${allocatingVoucher.voucher_no}`
              }
              onClose={() => setAllocatingVoucher(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
